// Design tokens for the whole game UI (menu, HUD, landing page, overlays).
//
// Two layers (see `docs/daisyui-migration-plan.md`):
//   1. a daisyUI theme name (`data-theme`), which supplies `--color-*`,
//      `--radius-*`, `--border`, `--depth`, `--noise`;
//   2. a flat map of *overrides* on top of it.
//
// `ui/app.css` aliases the game's `--ui-*` tokens onto the daisyUI variables, so
// changing the theme re-skins everything. The /design admin page edits both
// layers and publishes the result to `/api/theme`.

import { api } from "../base";
import { applyComponentStyles } from "./daisy";

export type ThemeGroup =
  | "DaisyUI colours"
  | "DaisyUI shape"
  | "Core colours"
  | "Accents"
  | "HUD & overlays"
  | "Typography"
  | "Spacing"
  | "Shape & layout";

export interface ThemeToken {
  /** CSS custom property name without the leading `--`. */
  id: string;
  label: string;
  group: ThemeGroup;
  type: "color" | "number" | "text" | "font";
  /** Shipped default. Absent for daisyUI variables, whose value comes from the
   *  active theme and is read back from the DOM. */
  value?: string;
  /** Number inputs: bounds + step + unit suffix. */
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  hint?: string;
}

// ---------------------------------------------------------------------------
// Theme catalogue (daisyUI 5 built-ins + ours)
// ---------------------------------------------------------------------------

/** The shipped theme, defined in `ui/theme.css` via `@plugin "daisyui/theme"`. */
export const DEFAULT_DAISY = "webrace";

export const DAISY_THEME_NAMES: string[] = [
  DEFAULT_DAISY,
  "light",
  "dark",
  "cupcake",
  "bumblebee",
  "emerald",
  "corporate",
  "synthwave",
  "retro",
  "cyberpunk",
  "valentine",
  "halloween",
  "garden",
  "forest",
  "aqua",
  "lofi",
  "pastel",
  "fantasy",
  "wireframe",
  "black",
  "luxury",
  "dracula",
  "cmyk",
  "autumn",
  "business",
  "acid",
  "lemonade",
  "night",
  "coffee",
  "winter",
  "dim",
  "nord",
  "sunset",
  "caramellatte",
  "abyss",
  "silk",
];

/** Semantic colours a daisyUI theme exposes. */
export const DAISY_COLORS: ThemeToken[] = [
  { id: "color-base-100", label: "Base 100 · page", group: "DaisyUI colours", type: "color" },
  { id: "color-base-200", label: "Base 200 · panel", group: "DaisyUI colours", type: "color" },
  { id: "color-base-300", label: "Base 300 · raised", group: "DaisyUI colours", type: "color" },
  { id: "color-base-content", label: "Base content · text", group: "DaisyUI colours", type: "color" },
  { id: "color-primary", label: "Primary · accent", group: "DaisyUI colours", type: "color" },
  { id: "color-primary-content", label: "Primary content", group: "DaisyUI colours", type: "color" },
  { id: "color-secondary", label: "Secondary", group: "DaisyUI colours", type: "color" },
  { id: "color-secondary-content", label: "Secondary content", group: "DaisyUI colours", type: "color" },
  { id: "color-accent", label: "Accent · info bright", group: "DaisyUI colours", type: "color" },
  { id: "color-accent-content", label: "Accent content", group: "DaisyUI colours", type: "color" },
  { id: "color-neutral", label: "Neutral", group: "DaisyUI colours", type: "color" },
  { id: "color-neutral-content", label: "Neutral content", group: "DaisyUI colours", type: "color" },
  { id: "color-info", label: "Info", group: "DaisyUI colours", type: "color" },
  { id: "color-info-content", label: "Info content", group: "DaisyUI colours", type: "color" },
  { id: "color-success", label: "Success · primary action", group: "DaisyUI colours", type: "color" },
  { id: "color-success-content", label: "Success content", group: "DaisyUI colours", type: "color" },
  { id: "color-warning", label: "Warning", group: "DaisyUI colours", type: "color" },
  { id: "color-warning-content", label: "Warning content", group: "DaisyUI colours", type: "color" },
  { id: "color-error", label: "Error", group: "DaisyUI colours", type: "color" },
  { id: "color-error-content", label: "Error content", group: "DaisyUI colours", type: "color" },
];

/** Shape knobs daisyUI exposes per theme. */
export const DAISY_SHAPE: ThemeToken[] = [
  {
    id: "radius-selector",
    label: "Corner radius · selectors",
    group: "DaisyUI shape",
    type: "number",
    value: "0px",
    min: 0,
    max: 24,
    step: 1,
    unit: "px",
    hint: "Checkboxes, toggles, badges.",
  },
  {
    id: "radius-field",
    label: "Corner radius · fields",
    group: "DaisyUI shape",
    type: "number",
    value: "0px",
    min: 0,
    max: 24,
    step: 1,
    unit: "px",
    hint: "Buttons, inputs, selects, tabs. Also drives `--ui-radius`.",
  },
  {
    id: "radius-box",
    label: "Corner radius · boxes",
    group: "DaisyUI shape",
    type: "number",
    value: "0px",
    min: 0,
    max: 24,
    step: 1,
    unit: "px",
    hint: "Cards, modals, alerts.",
  },
  {
    id: "border",
    label: "Border width",
    group: "DaisyUI shape",
    type: "number",
    value: "1px",
    min: 0,
    max: 4,
    step: 1,
    unit: "px",
  },
  {
    id: "size-field",
    label: "Fields base size",
    group: "DaisyUI shape",
    type: "number",
    value: "4px",
    min: 2,
    max: 8,
    step: 0.5,
    unit: "px",
    hint: "Drives button/input padding via the size scale.",
  },
  {
    id: "size-selector",
    label: "Selectors base size",
    group: "DaisyUI shape",
    type: "number",
    value: "4px",
    min: 2,
    max: 8,
    step: 0.5,
    unit: "px",
    hint: "Checkboxes, radios, toggles, badges.",
  },
  {
    id: "depth",
    label: "Depth",
    group: "DaisyUI shape",
    type: "number",
    value: "0",
    min: 0,
    max: 1,
    step: 1,
    hint: "0 or 1 — adds a subtle 3D shadow to daisyUI components.",
  },
  {
    id: "noise",
    label: "Noise",
    group: "DaisyUI shape",
    type: "number",
    value: "0",
    min: 0,
    max: 1,
    step: 1,
    hint: "0 or 1 — grainy texture on daisyUI components.",
  },
];

/**
 * wbrace's own tokens. Most are literals (or derived in `app.css`); only the
 * ones a human should tune on their own are listed here.
 */
export const THEME_TOKENS: ThemeToken[] = [
  // ---- Core colours -------------------------------------------------------
  { id: "ui-hud-health", label: "Health", group: "HUD & overlays", type: "color", value: "#5ce27a" },
  { id: "ui-hud-armor", label: "Armor / spectate", group: "HUD & overlays", type: "color", value: "#7fd4ff" },
  { id: "ui-hud-ammo", label: "Ammo", group: "HUD & overlays", type: "color", value: "#ffd23f" },
  { id: "ui-gold", label: "Gold / system message", group: "HUD & overlays", type: "color", value: "#ffd24d" },
  { id: "ui-net", label: "Netgraph", group: "HUD & overlays", type: "color", value: "#8fe3c0" },
  { id: "ui-team-red", label: "Team / player red", group: "HUD & overlays", type: "color", value: "#ff6b6b" },
  { id: "ui-team-blue", label: "Team / player blue", group: "HUD & overlays", type: "color", value: "#6bb6ff" },
  { id: "ui-spectate", label: "Spectate", group: "HUD & overlays", type: "color", value: "#7fd4ff" },
  { id: "ui-console-bg", label: "Console background", group: "HUD & overlays", type: "color", value: "#04060a" },
  { id: "ui-console-text", label: "Console text", group: "HUD & overlays", type: "color", value: "#d7dbe4" },
  { id: "ui-danger", label: "Danger (game orange)", group: "HUD & overlays", type: "color", value: "#e8603a" },

  // ---- Typography --------------------------------------------------------
  {
    id: "ui-font-family",
    label: "Font family",
    group: "Typography",
    type: "font",
    value: "ui-monospace, monospace",
    hint: "Any CSS font stack, e.g. `ui-monospace, monospace` or `system-ui, sans-serif`.",
  },
  { id: "ui-font-size", label: "Base font size", group: "Typography", type: "number", value: "13px", min: 9, max: 22, step: 1, unit: "px" },
  { id: "ui-font-size-sm", label: "Small font size", group: "Typography", type: "number", value: "12px", min: 8, max: 20, step: 1, unit: "px" },
  { id: "ui-font-size-xs", label: "Tiny font size", group: "Typography", type: "number", value: "11px", min: 7, max: 18, step: 1, unit: "px" },
  { id: "ui-line-height", label: "Line height", group: "Typography", type: "number", value: "1.5", min: 1, max: 2.2, step: 0.05 },


  // ---- Typography roles (empty = follow the base font) --------------------
  { id: "ui-font-heading", label: "Headings · font", group: "Typography", type: "font", value: "", hint: "h1–h5, page titles and card titles. Empty = base font." },
  { id: "ui-size-heading", label: "Headings · size", group: "Typography", type: "number", value: "", min: 8, max: 40, step: 1, unit: "px", hint: "Empty = keep each element's own size." },
  { id: "ui-font-title", label: "Landing title · font", group: "Typography", type: "font", value: "" },
  { id: "ui-size-title", label: "Landing title · size", group: "Typography", type: "number", value: "", min: 12, max: 64, step: 1, unit: "px" },
  { id: "ui-font-menu", label: "Menu items · font", group: "Typography", type: "font", value: "" },
  { id: "ui-size-menu", label: "Menu items · size", group: "Typography", type: "number", value: "", min: 8, max: 24, step: 1, unit: "px" },
  { id: "ui-font-body", label: "Paragraphs · font", group: "Typography", type: "font", value: "" },
  { id: "ui-size-body", label: "Paragraphs · size", group: "Typography", type: "number", value: "", min: 8, max: 24, step: 1, unit: "px" },
  { id: "ui-font-label", label: "Labels · font", group: "Typography", type: "font", value: "" },
  { id: "ui-size-label", label: "Labels · size", group: "Typography", type: "number", value: "", min: 8, max: 24, step: 1, unit: "px" },
  { id: "ui-font-button", label: "Buttons · font", group: "Typography", type: "font", value: "" },
  { id: "ui-size-button", label: "Buttons · size", group: "Typography", type: "number", value: "", min: 8, max: 24, step: 1, unit: "px" },

  // ---- Spacing -----------------------------------------------------------
  { id: "ui-space", label: "Spacing / gaps", group: "Spacing", type: "number", value: "8px", min: 2, max: 24, step: 1, unit: "px", hint: "Gaps between controls and sections." },
  { id: "ui-pad-x", label: "Control horizontal padding", group: "Spacing", type: "number", value: "12px", min: 4, max: 28, step: 1, unit: "px" },
  { id: "ui-pad-y", label: "Control vertical padding", group: "Spacing", type: "number", value: "7px", min: 2, max: 18, step: 1, unit: "px" },
  { id: "ui-card-pad", label: "Card padding", group: "Spacing", type: "number", value: "20px", min: 8, max: 48, step: 1, unit: "px", hint: "Padding inside the landing card and inner panels." },
  { id: "ui-page-margin", label: "Page margin", group: "Spacing", type: "number", value: "24px", min: 8, max: 64, step: 2, unit: "px", hint: "Outer margin of the landing page and menu." },

  // ---- Shape & layout ----------------------------------------------------
  { id: "ui-menu-width", label: "Menu width", group: "Shape & layout", type: "number", value: "640px", min: 480, max: 1100, step: 10, unit: "px" },
  { id: "ui-overlay-opacity", label: "Menu overlay opacity", group: "Shape & layout", type: "number", value: "0.96", min: 0.5, max: 1, step: 0.01 },
  { id: "ui-backdrop-blur", label: "Menu backdrop blur", group: "Shape & layout", type: "number", value: "6px", min: 0, max: 24, step: 1, unit: "px", hint: "Softens the game behind the menu. 0 = sharp see-through." },
];

export const THEME_GROUPS: ThemeGroup[] = [
  "DaisyUI colours",
  "DaisyUI shape",
  "HUD & overlays",
  "Typography",
  "Spacing",
  "Shape & layout",
];

/** Every token the editor can write, in section order. */
export const ALL_TOKENS: ThemeToken[] = [
  ...DAISY_COLORS,
  ...DAISY_SHAPE,
  ...THEME_TOKENS,
];

const TOKEN_BY_ID = new Map(ALL_TOKENS.map((t) => [t.id, t]));

export type ThemeValues = Record<string, string>;

/** Background image URLs allowed in a published theme (http(s) or site-absolute). */
export function isSharedImageUrl(value: string): boolean {
  const url = value.trim();
  if (!url || url.length > 300) return false;
  if (!/^https?:\/\/|^\//.test(url)) return false;
  if (/["'()<>\\\s;{}]/.test(url)) return false;
  return true;
}

/** Per-component style decision picked on the /design "Components" tab. */
export interface StyleChoice {
  /** Variant id from the catalogue (solid, soft, outline, …). */
  style: string;
  /** daisyUI colour suffix (primary, success, …) or "". */
  color: string;
  /** daisyUI size suffix (xs…xl) or "". */
  size: string;
  /** Structural variant id (text, horizontal, square, wide, …) or "". */
  shape: string;
  /** Extra option groups (card padding, background, image, …): group id -> choice id. */
  options?: Record<string, string>;
  /** Custom background image URL (components that declare `imageField`). */
  image?: string;
}

export interface ThemeDoc {
  /** daisyUI theme name applied via `data-theme`. */
  daisy: string;
  /** Overrides only — keys are CSS variable names without `--`. */
  tokens: ThemeValues;
  /** Component id -> chosen style. Empty string fields mean "daisyUI default". */
  styles: Record<string, StyleChoice>;
}

export const DEFAULT_DOC: ThemeDoc = { daisy: DEFAULT_DAISY, tokens: {}, styles: {} };

/** Shipped defaults for the wbrace tokens (absent = "no override"). */
export const DEFAULT_THEME: ThemeValues = Object.fromEntries(
  THEME_TOKENS.filter((t) => t.value !== undefined).map((t) => [t.id, t.value as string]),
);

/** localStorage key for the last theme fetched from the server (fast startup). */
const CACHE_KEY = "webrace.theme.cache";
/** localStorage key for the admin publish token on this browser. */
export const ADMIN_TOKEN_KEY = "webrace.theme.admin";

function coerce(token: ThemeToken, raw: unknown): string | null {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const value = String(raw).trim();
  if (!value || value.length > 200) return null;
  if (token.type === "color") {
    return /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
  }
  if (token.type === "number") {
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return null;
    const lo = token.min ?? -Infinity;
    const hi = token.max ?? Infinity;
    const clamped = Math.min(hi, Math.max(lo, n));
    return `${clamped}${token.unit ?? ""}`;
  }
  return value;
}

/** v1 flat map -> daisyUI colour overrides, so an old published theme survives. */
const LEGACY_COLOR_MAP: Record<string, string> = {
  "ui-bg": "color-base-100",
  "ui-panel": "color-base-200",
  "ui-panel-raised": "color-base-300",
  "ui-text": "color-base-content",
  "ui-accent": "color-primary",
  "ui-success": "color-success",
  "ui-info": "color-info",
  "ui-warn": "color-warning",
  "ui-error": "color-error",
  "ui-info-bright": "color-accent",
};

/**
 * Normalise anything we might have stored or been sent into a `ThemeDoc`.
 *
 * Accepts the v2 shape (`{ v: 2, daisy, tokens }`), a `{ theme: … }` wrapper,
 * and legacy v1 flat maps (`{ "ui-bg": "#0b0d12", … }`). Unknown keys and
 * invalid values are dropped; values equal to a shipped default are dropped
 * too, because they are not overrides.
 *
 * A v1 map carries colours that are now *derived* from the daisyUI theme, so
 * the readable subset is migrated onto the matching `--color-*` variables —
 * otherwise an already-published theme would lose its palette on upgrade.
 */
export function normalizeTheme(input: unknown): ThemeDoc {
  const doc: ThemeDoc = { daisy: DEFAULT_DAISY, tokens: {}, styles: {} };
  let src: unknown = input;
  if (src && typeof src === "object" && !Array.isArray(src)) {
    const wrapped = (src as Record<string, unknown>).theme;
    if (wrapped && typeof wrapped === "object") src = wrapped;
  }
  if (!src || typeof src !== "object" || Array.isArray(src)) return doc;
  const o = src as Record<string, unknown>;

  if (typeof o.daisy === "string" && DAISY_THEME_NAMES.includes(o.daisy)) {
    doc.daisy = o.daisy;
  }
  const isDoc = o.v === 2 || typeof o.daisy === "string" || (o.tokens && typeof o.tokens === "object");
  const rawTokens = o.tokens && typeof o.tokens === "object" ? o.tokens : o;

  for (const [id, raw] of Object.entries(rawTokens as Record<string, unknown>)) {
    const token = TOKEN_BY_ID.get(id);
    if (!token) continue;
    const value = coerce(token, raw);
    if (value === null) continue;
    if (token.value !== undefined && value === token.value) continue;
    doc.tokens[id] = value;
  }

  const rawStyles = o.styles;
  if (rawStyles && typeof rawStyles === "object" && !Array.isArray(rawStyles)) {
    for (const [key, value] of Object.entries(rawStyles as Record<string, unknown>)) {
      if (!/^[a-z][a-z0-9-]{0,23}$/.test(key)) continue;
      if (!value || typeof value !== "object" || Array.isArray(value)) continue;
      const v = value as Record<string, unknown>;
      const field = (x: unknown) =>
        typeof x === "string" && /^[a-z0-9-]{0,24}$/.test(x) ? x : "";
      const choice: StyleChoice = {
        style: field(v.style),
        color: field(v.color),
        size: field(v.size),
        shape: field(v.shape),
      };
      if (v.options && typeof v.options === "object" && !Array.isArray(v.options)) {
        const options: Record<string, string> = {};
        for (const [gid, gval] of Object.entries(v.options as Record<string, unknown>)) {
          if (!/^[a-z][a-z0-9-]{0,23}$/.test(gid)) continue;
          const id = field(gval);
          if (id) options[gid] = id;
        }
        if (Object.keys(options).length) choice.options = options;
      }
      if (typeof v.image === "string" && isSharedImageUrl(v.image)) {
        choice.image = v.image.trim();
      }
      if (choice.style || choice.color || choice.size || choice.shape ||
          choice.options || choice.image) {
        doc.styles[key] = choice;
      }
    }
  }

  if (!isDoc) {
    for (const [from, to] of Object.entries(LEGACY_COLOR_MAP)) {
      const raw = (rawTokens as Record<string, unknown>)[from];
      if (typeof raw !== "string" || !/^#[0-9a-f]{6}$/i.test(raw)) continue;
      if (doc.tokens[to] === undefined) doc.tokens[to] = raw.toLowerCase();
    }
  }
  return doc;
}

/** Apply a theme to the current document: theme name + inline overrides. */
export function applyTheme(doc: ThemeDoc): void {
  const root = document.documentElement;
  if (doc.daisy && DAISY_THEME_NAMES.includes(doc.daisy)) {
    root.dataset.theme = doc.daisy;
  }
  for (const token of ALL_TOKENS) root.style.removeProperty(`--${token.id}`);
  for (const token of ALL_TOKENS) {
    const value = doc.tokens[token.id];
    if (value !== undefined) root.style.setProperty(`--${token.id}`, value);
  }
  applyComponentStyles(doc.styles ?? {});
}

/** Remove all inline overrides (back to the theme's own values). */
export function clearTheme(): void {
  const root = document.documentElement;
  for (const token of ALL_TOKENS) root.style.removeProperty(`--${token.id}`);
}

/** Theme cached from the server (or edited locally) in this browser. */
export function loadCachedTheme(): ThemeDoc {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return normalizeTheme(JSON.parse(raw));
  } catch {
    /* ignore */
  }
  return { daisy: DEFAULT_DAISY, tokens: {}, styles: {} };
}

export function cacheTheme(doc: ThemeDoc): void {
  try {
    // Cache only overrides: the pre-paint script in index.html replays this
    // verbatim, and inline defaults would mask the active daisyUI theme.
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ v: 2, daisy: doc.daisy, tokens: doc.tokens, styles: doc.styles }),
    );
  } catch {
    /* ignore */
  }
}

export function clearCachedTheme(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Fetch the published global theme.
 *
 * Returns `null` when the backend is unreachable (keep the local cache), and
 * `{ theme: null }` when the backend answered but no theme is published (reset
 * to the shipped defaults). That distinction matters: a deleted theme must not
 * leave visitors stuck on a stale cached design.
 */
export async function fetchTheme(): Promise<{
  theme: ThemeDoc | null;
  updatedAt?: string;
} | null> {
  try {
    const r = await fetch(api("/api/theme"), { cache: "no-store" });
    if (!r.ok) return null;
    const data = (await r.json()) as { theme?: unknown; updatedAt?: string };
    return {
      theme: data?.theme ? normalizeTheme(data.theme) : null,
      updatedAt: data?.updatedAt,
    };
  } catch {
    return null;
  }
}

let refreshInFlight: Promise<void> | null = null;

/**
 * Startup/refresh path: apply the server theme if one is published and cache it.
 * Never blocks or throws.
 *
 * A successful empty response (`theme: null`) deliberately does **not** wipe the
 * cached theme. The game should keep the last published look instead of
 * flashing the default palette if the backend briefly has no file (container
 * restart, deploy, transient storage glitch). Explicit resets live in the
 * design page, which has its own handling.
 */
export function refreshThemeFromServer(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const remote = await fetchTheme();
    if (!remote) return; // offline: keep the cached copy
    if (remote.theme) {
      applyTheme(remote.theme);
      cacheTheme(remote.theme);
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Publish a theme globally. The admin token is checked by the server. */
export async function publishTheme(
  doc: ThemeDoc,
  token: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!token) return { ok: false, error: "enter the admin token" };
  try {
    const r = await fetch(api("/api/theme"), {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-theme-token": token },
      body: JSON.stringify({
        v: 2,
        daisy: doc.daisy,
        tokens: normalizeTheme(doc).tokens,
        styles: normalizeTheme(doc).styles,
      }),
    });
    if (!r.ok) {
      const data = (await r.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? `save failed (${r.status})` };
    }
    cacheTheme(doc);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String((e as Error)?.message ?? e) };
  }
}

/**
 * JSON export/import (the format accepted by `publishTheme`).
 */
export function themeToJson(doc: ThemeDoc): string {
  return JSON.stringify(
    { v: 2, daisy: doc.daisy, tokens: doc.tokens, styles: doc.styles },
    null,
    2,
  );
}

/**
 * Ready-to-commit CSS: a complete `@plugin "daisyui/theme"` block merged with
 * the live overrides, so the /design page doubles as a theme generator.
 *
 * `effective(id)` must return the *resolved* value of a CSS variable (the
 * design page reads it from the DOM).
 */
export function themeToPluginCss(
  doc: ThemeDoc,
  effective: (id: string) => string,
  colorScheme: "light" | "dark" = "dark",
): string {
  const lines: string[] = [];
  lines.push(`@plugin "daisyui/theme" {`);
  lines.push(`  name: "webrace";`);
  lines.push(`  default: true;`);
  lines.push(`  prefersdark: false;`);
  lines.push(`  color-scheme: ${colorScheme};`);
  for (const token of [...DAISY_COLORS, ...DAISY_SHAPE]) {
    const value = effective(token.id) || doc.tokens[token.id] || token.value || "";
    if (!value) continue;
    lines.push(`  --${token.id}: ${value};`);
  }
  lines.push(`}`);
  const extras = THEME_TOKENS.filter((t) => doc.tokens[t.id] !== undefined);
  if (extras.length) {
    lines.push("");
    lines.push(`/* wbrace-specific overrides */`);
    lines.push(`:root {`);
    for (const token of extras) lines.push(`  --${token.id}: ${doc.tokens[token.id]};`);
    lines.push(`}`);
  }
  return lines.join("\n") + "\n";
}
